import {AfterViewInit, Component, ElementRef, EventEmitter, Inject, OnDestroy, OnInit, Output, ViewChild} from '@angular/core';
import {AbstractDialogModel} from '@shared/dialogs/model/abstract-dialog.model';
import {TtsControlEnum} from '@core/enums/tts-control.enum';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {FileRestService} from '@core/modules/rest/file/file-rest.service';
import {TtsHelperService} from '@shared/utils/tts-helper.service';
import {TtsRestService} from '@core/modules/rest/text-to-speech/tts-rest.service';
import {SettingsService} from '@shared/utils/settings.service';
import {PopupTypeEnum} from '@core/enums/popup-type.enum';
import {untilDestroyed} from '@ngneat/until-destroy';
import {UnitPopupComponent} from '@shared/dialogs/unit-popup/unit-popup.component';
import {createClickEventForPopUp} from '../../../../views/secured/views/modules/display-consent/helpers/helper-popup';
import {ReadingContentTypeEnum} from '@core/enums/reading-content-type.enum';
import {UnitInformationService} from '@shared/utils/unit-information.service';

@Component({
    selector: 'app-unit-popup-with-popup',
    templateUrl: './unit-popup-with-popup.component.html',
    styleUrls: ['./unit-popup-with-popup.component.scss']
})
export class UnitPopupWithPopupComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('focusArrow', {static: false}) focusArrow?: ElementRef;
    @ViewChild('showImage', {static: false}) showImage;
    @Output() openDialog: EventEmitter<AbstractDialogModel> = new EventEmitter();
    dialogWidth: string | undefined;
    data: AbstractDialogModel | undefined;
    ttsIconState = TtsControlEnum.playState;

    imageIds: string[] = [];

    audio = new Audio();
    firstPlay = true;
    audioWasPlayedInComponent = false;
    ttsUrl: string | undefined;
    playButtonActive = true;
    repeatButtonActive = false;
    textSize: string;
    description: string;

    constructor(private dialog: MatDialogRef<UnitPopupComponent>,
                @Inject(MAT_DIALOG_DATA) data: any,
                private fileRestService: FileRestService,
                private ttsHelperService: TtsHelperService,
                private ttsService: TtsRestService,
                private settingsService: SettingsService,
                private unitHelper: UnitInformationService) {
        this.data = data;
        this.playButtonActive = !this.data.disableVoice;
    }

    ngOnInit(): void {
        this.description = this.data.content;
        this.textSize = this.settingsService.addFontClass(this.data.textSize);
        this.audio.addEventListener('ended', this.setButtonOnEnd, false);
        if (this.data?.type === PopupTypeEnum.MULTIMEDIA) {
            this.imageIds = this.data?.id.split('_');
        }
    }

    ngAfterViewInit(): void {
        const navigationArrowNext = this.focusArrow?.nativeElement.querySelector('div.carousel-arrow-next');
        const navigationArrowPrev = this.focusArrow?.nativeElement.querySelector('div.carousel-arrow-prev');
        if (navigationArrowNext !== null) {
            navigationArrowNext.setAttribute('tabindex', '0');
            navigationArrowNext.addEventListener('keyup', (event: KeyboardEvent) => this.onKeyUpShowNext(event));
            navigationArrowNext.addEventListener('click', _ => this.onMouseClickShowNext());
        }
        if (navigationArrowPrev !== null) {
            navigationArrowPrev.setAttribute('tabindex', '0');
            navigationArrowPrev.addEventListener('keyup', (event: KeyboardEvent) => this.onKeyUpShowPrev(event));
            navigationArrowPrev.addEventListener('click', _ => this.onMouseClickShowPrev());
        }

        const pTag: any = document.querySelector('.content__text__info');
        pTag.innerHTML = this.description;

        const images = document.querySelectorAll('.content__text .inline-image');
        this.unitHelper.getInlineImage(images);
        const videos = document.querySelectorAll('.content__text .inline-video');
        this.unitHelper.getInlineVideos(videos);

        const textPopUp = document.querySelectorAll('.popup-link');
        createClickEventForPopUp(textPopUp, this.openDialog, null);

        const multimediaPopUp = document.querySelectorAll('.multimedia-link');
        createClickEventForPopUp(multimediaPopUp, this.openDialog, this.data.indicationId);
    }

    onKeyUpShowNext(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.onMouseClickShowNext();
        }
    }

    private onMouseClickShowNext(): void {
        const showImageWithId = this.imageIds[this.showImage.carousel.slide.counter + 1];
        this.showImage.next();
        this.loadDescription(showImageWithId, this.data.indicationId);
    }

    onKeyUpShowPrev(event: KeyboardEvent): void {
        if (event.key === 'Enter' || event.code === 'Space') {
            this.onMouseClickShowPrev();
        }
    }

    private onMouseClickShowPrev(): void {
        const showImageWithId = this.imageIds[this.showImage.carousel.slide.counter - 1];
        this.showImage.prev();
        this.loadDescription(showImageWithId, this.data.indicationId);
    }

    loadDescription(imageId: string, indicationId: string): void {
        this.fileRestService.getPopupMultimediaImageDescription(imageId, indicationId, this.data.language).subscribe(response => {
            this.description = response;
        });
    }

    convertTextToSpeech(): void {
        const removedHtmlTagsTitle = this.ttsHelperService.removeHTMLTags(this.data?.title);
        const removedHtmlTagsInfo = this.ttsHelperService.removeSSML(this.data?.content);
        const finalText = removedHtmlTagsTitle.concat('. ').concat(removedHtmlTagsInfo);

        const pollyObject = {
            text: finalText,
            readingSpeed: this.ttsHelperService.getReadingSpeedByLanguageStyle(this.data?.languageStyle),
            readingLanguage: this.data?.language,
            readingContentType: ReadingContentTypeEnum.TEXT
        };

        this.firstPlay = false;
        this.repeatButtonActive = true;
        this.ttsService.getTTS(pollyObject).pipe(untilDestroyed(this)).subscribe((data: any) => {
            this.ttsUrl = data;
            if (this.ttsUrl) {
                this.audio.src = this.ttsUrl;
                this.playAudio();
                this.audioWasPlayedInComponent = true;
            }
        });
    }

    readText(): void {
        if (this.firstPlay) {
            this.ttsIconState = TtsControlEnum.pauseState;
            this.convertTextToSpeech();
        } else {
            if (this.audio.ended) {
                this.playAudio();
            } else {
                if (this.ttsIconState === TtsControlEnum.playState) {
                    this.resumeAudio();
                } else {
                    this.pauseAudio();
                }
            }
        }
    }

    playAudio(): void {
        this.audio.load();
        this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
    }

    pauseAudio(): void {
        this.ttsIconState = TtsControlEnum.playState;
        if (!this.audio.ended) {
            this.audio.pause();
        }
    }

    resumeAudio(): void {
        if (!this.audio.ended) {
            if (this.ttsIconState === TtsControlEnum.pauseState) {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            } else {
                this.audio.play().then(() => this.ttsIconState = TtsControlEnum.pauseState);
            }
        }
    }

    setButtonOnEnd = (evt: { type: string; }) => {
        if (evt.type === 'ended') {
            this.ttsIconState = TtsControlEnum.playState;
        }
    }

    hasImages(): boolean {
        return this.imageIds.length > 0;
    }

    showNavigation(): boolean {
        return this.imageIds.length > 1;
    }

    ngOnDestroy(): void {
        this.pauseAudio();
    }

}
